// Piezas compartidas de la ficha 360 del cliente. Existen para que las cuatro pestañas digan un
// hueco de la MISMA forma: nunca un cero, nunca un guion mudo, siempre con el motivo a la mano.
import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import type { Gap } from '../lib/customer360Gaps';

export const CHIP =
    'inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9.5px] font-semibold';

export const CARD = 'rounded-lg border border-border bg-card';

export const CARD_HEAD =
    'border-b border-border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground';

export const KV_GRID = 'grid grid-cols-2 gap-x-3 gap-y-2 p-3 text-[11.5px]';

/**
 * Caja del modal de detalle que abre SOBRE el cajón. El alto sale del mock (`ficha-credito.css`) y
 * la segunda fila de la reja es la que scrollea: la página de atrás nunca se mueve.
 * El `pr` del encabezado le reserva su hueco a la X del diálogo, que va flotando en la esquina.
 */
export const FILE_MODAL_CLASS =
    'grid-rows-[auto_minmax(0,1fr)] h-[85vh] max-h-[820px] min-h-[500px] gap-3 overflow-hidden p-4 sm:p-4 [&>*:first-child]:pr-8';

/**
 * El marco de la región de contenido del modal: la delimita del chasis y recorta lo que se salga.
 * No lleva sangría propia — la ficha que va adentro trae la suya — y el scroll vive aquí dentro.
 */
export function FileModalFrame({ children }: { children: ReactNode }) {
    return (
        <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-background">
            {children}
        </div>
    );
}

/** El disparador del detalle: mismo aspecto en la tabla de créditos y en la tarjeta del equipo. */
export const FILE_DETAIL_TRIGGER =
    'whitespace-nowrap rounded text-[10.5px] font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** Una tarjeta de la ficha, con su título y su rejilla de campos. */
export function FileCard({
    title,
    meta,
    children,
    grid = true,
}: {
    title: string;
    meta?: string;
    children: ReactNode;
    /** `false` deja el cuerpo suelto: para la nota operativa y las listas. */
    grid?: boolean;
}) {
    return (
        <section className={CARD}>
            <h3 className={cn(CARD_HEAD, 'flex items-baseline justify-between gap-2')}>
                <span>{title}</span>
                {meta ? <span className="font-normal normal-case">{meta}</span> : null}
            </h3>
            {grid ? <dl className={KV_GRID}>{children}</dl> : <div className="p-3">{children}</div>}
        </section>
    );
}

export function Field({
    label,
    title,
    wrap = false,
    children,
}: {
    label: string;
    /** Por qué el rótulo dice lo que dice. Para los que el silo no alcanzó a nombrar. */
    title?: string;
    /** `true` deja que el valor baje de renglón: para el texto que cortado diría otra cosa. */
    wrap?: boolean;
    children: ReactNode;
}) {
    return (
        <div className="min-w-0">
            <dt
                title={title}
                className={cn(
                    'text-[9px] font-bold uppercase tracking-[0.06em] text-muted-foreground',
                    title ? 'cursor-help' : '',
                )}
            >
                {label}
            </dt>
            <dd className={cn('mt-0.5', wrap ? 'break-words' : 'truncate')}>{children}</dd>
        </div>
    );
}

/** Igual que `Field` pero a lo ancho de la rejilla: para el domicilio y los textos largos. */
export function WideField({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="col-span-2 min-w-0">
            <dt className="text-[9px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                {label}
            </dt>
            <dd className="mt-0.5">{children}</dd>
        </div>
    );
}

/**
 * El dato viene EN CAMINO. Pintar aquí «no disponible» es el mismo bug que pintar un cero: afirma
 * una ausencia que todavía no se sabe.
 */
export function LoadingValue({ subject }: { subject: string }) {
    return (
        <span title={`Consultando ${subject}…`} className="cursor-help text-muted-foreground">
            …
        </span>
    );
}

/** Un hueco LEÍDO: el dato llegó vacío, y la leyenda dice qué significa que lo esté. */
export function ReadGap({ gap, className }: { gap: Gap; className?: string }) {
    return (
        <span
            title={gap.note}
            className={cn('cursor-help italic text-muted-foreground', className)}
        >
            {gap.label}
        </span>
    );
}

/**
 * Un dato que NO SE LEYÓ: la lectura falló o ni salió. Es el tercer hueco y el que faltaba —
 * decirlo como `ReadGap` acusa al alta de no registrar lo que nadie llegó a pedir.
 */
export function UnreadValue({ gap, className }: { gap: Gap; className?: string }) {
    return (
        <span
            title={gap.note}
            className={cn('cursor-help italic text-muted-foreground/70', className)}
        >
            {gap.label}
        </span>
    );
}

/** Un dato que NINGÚN endpoint de esta ficha entrega, con SU motivo a la vista. */
export function NoSourceValue({ gap, className }: { gap: Gap; className?: string }) {
    return (
        <span
            title={gap.note}
            className={cn('cursor-help italic text-muted-foreground/70', className)}
        >
            {gap.label}
        </span>
    );
}

/**
 * Un hecho LEÍDO que deja una pestaña vacía. Va en bloque neutro: `ErrorState` es `role="alert"`
 * con triángulo rojo y queda SOLO para la lectura que falló o que no se permitió.
 */
export function ReadFactBlock({ title, description }: { title: string; description: string }) {
    return (
        <section className="rounded-lg border border-dashed border-border bg-muted/30 p-4 text-[11.5px] leading-relaxed text-muted-foreground">
            <div className="text-[12px] font-semibold text-foreground">{title}</div>
            <p className="mt-1">{description}</p>
        </section>
    );
}

/** Cuánto se queda el `✓` antes de volver a ofrecer copiar. */
const COPIED_RESET_MS = 2_000;

/**
 * Copia al portapapeles el valor de una celda de la ficha. Solo se pinta junto a un valor real: al
 * lado de un hueco copiaría el motivo del hueco.
 */
export function CopyButton({ value, subject }: { value: string; subject: string }) {
    // Se guarda el VALOR copiado, no un `true`: al cambiar de cliente la celda trae otro valor y el
    // `✓` se apaga solo, sin un efecto que lo persiga.
    const [copiedValue, setCopiedValue] = useState<string | null>(null);
    const copied = copiedValue === value;

    useEffect(() => {
        if (!copied) return;
        const timer = window.setTimeout(() => {
            setCopiedValue(null);
        }, COPIED_RESET_MS);
        return () => {
            window.clearTimeout(timer);
        };
    }, [copied]);

    return (
        <>
            <button
                type="button"
                aria-label={`Copiar ${subject}`}
                title={`Copiar ${subject}`}
                onClick={() => {
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- el tipo DOM marca clipboard como siempre presente, pero falta en contextos no seguros / jsdom
                    if (!navigator.clipboard) return;
                    // El `✓` solo se prende si la copia RESOLVIÓ: prenderlo siempre le diría al
                    // operador que ya tiene en el portapapeles algo que nunca llegó.
                    void navigator.clipboard.writeText(value).then(
                        () => {
                            setCopiedValue(value);
                        },
                        () => {
                            setCopiedValue(null);
                        },
                    );
                }}
                className="ml-1.5 rounded-sm border border-border bg-card px-1.5 py-px align-middle text-[9px] font-normal text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
                {copied ? '✓' : 'copiar'}
            </button>
            <span className="sr-only" role="status" aria-live="polite">
                {copied ? `Copiado al portapapeles: ${subject}` : ''}
            </span>
        </>
    );
}

/** El pie de una tarjeta: lo que esa tarjeta NO alcanza a decir, y por qué. */
export function CardFootnote({ children }: { children: ReactNode }) {
    return <p className="px-3 pb-2.5 text-[10px] leading-snug text-muted-foreground">{children}</p>;
}
